import { Link } from "react-router-dom";
import {
  ArrowRight,
  ShieldCheck,
  Users,
  Check,
  Compass,
  Layers,
} from "lucide-react";
import usePageMeta from "../../lib/usePageMeta";
import {
  HERO,
  STATS,
  INDUSTRIES,
  WORKPLACE_REALITIES,
} from "../../data/publicSite";

export default function PublicHome() {
  usePageMeta({
    title: "California Corporate Chaplains | Workplace Chaplaincy in Sacramento, Placer, El Dorado & Nevada Counties",
    description: "Relational, non-clinical workplace chaplaincy from California Corporate Chaplains (CCCHAPS). Scheduled onsite service in Sacramento, Placer, El Dorado, and Nevada Counties, plus remote chaplain support across California and nationwide — with 24/7 on-call chaplain access.",
  });
  return (
    <div data-testid="public-home">
      {/* Hero */}
      <section className="relative bg-gradient-to-b from-[#F8FAFC] to-white" data-analytics-section="hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 md:pt-24 pb-16 md:pb-24 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div className="fade-up">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#F0F4F8] text-[#3B597B] text-xs font-medium mb-6">
              <ShieldCheck className="w-3.5 h-3.5" /> {HERO.eyebrow}
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-slate-900 text-balance">
              {HERO.title}
            </h1>
            <p className="mt-6 text-base sm:text-lg leading-relaxed text-slate-600 max-w-xl">{HERO.subtitle}</p>
            {HERO.nonClinicalNote && (
              <p className="mt-3 text-sm text-slate-500 leading-relaxed max-w-xl" data-testid="home-nonclinical-note">
                {HERO.nonClinicalNote}
              </p>
            )}
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link
                to={HERO.ctaPrimary.to}
                data-testid="hero-primary-cta"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg bg-[#4A6FA5] text-white font-medium hover:bg-[#3B597B] transition-colors"
              >
                {HERO.ctaPrimary.label}
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to={HERO.ctaSecondary.to}
                data-testid="hero-secondary-cta"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg bg-white border border-slate-300 text-slate-800 font-medium hover:border-[#4A6FA5] hover:text-[#4A6FA5] transition-colors"
              >
                {HERO.ctaSecondary.label}
              </Link>
            </div>
            <p className="mt-6 text-xs text-slate-500">
              Employees of enrolled organizations →{" "}
              <Link to="/care-portal" className="text-[#4A6FA5] hover:text-[#3B597B] font-medium">
                Access the Care Portal
              </Link>
            </p>
            <p className="mt-3 text-xs text-slate-500 leading-relaxed" data-testid="home-service-area">
              <span className="font-medium text-slate-700">Locally led in Greater Sacramento</span> — serving Sacramento, Placer, El Dorado &amp; Nevada Counties in person
              <span className="mx-1.5 text-slate-400">•</span>
              Remote support also available across California and nationwide
            </p>
          </div>
          <div className="relative fade-up-delay-2">
            <div
              className="aspect-[16/9] rounded-2xl overflow-hidden shadow-xl border border-slate-200 bg-[#F0F4F8] flex items-center justify-center"
              data-testid="hero-image-wrap"
            >
              <img
                src={HERO.image}
                alt={HERO.imageAlt}
                className="w-full h-full object-contain"
                data-testid="hero-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Business Owners reassurance (item 3) */}
      <section
        className="py-16 md:py-20 bg-white border-b border-slate-200"
        data-testid="home-business-owners"
        aria-labelledby="home-business-owners-heading"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-5 gap-10 lg:gap-14">
          <div className="lg:col-span-3">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-3">For business owners</p>
            <h2
              id="home-business-owners-heading"
              className="font-heading text-3xl sm:text-4xl tracking-tight text-slate-900 leading-tight"
            >
              You Care for Your Business. We Help You Care for Your People.
            </h2>
            <div className="mt-5 space-y-4 text-slate-700 leading-relaxed">
              <p>
                Running a business means caring about more than operations and productivity. Employees experience grief, family challenges,
                emotional stress, personal crises, and unexpected life transitions. California Corporate Chaplains provides compassionate,
                confidential workplace support that helps employees feel heard, respected, and supported during difficult times.
              </p>
              <p>
                Our services complement existing Human Resources and Employee Assistance Programs. They are voluntary, non-clinical, and
                available to employees of all faith traditions and those with no religious affiliation.
              </p>
            </div>
            <div className="mt-7 flex flex-col sm:flex-row gap-3" data-testid="home-business-owners-ctas">
              <Link
                to="/schedule?type=organizational_consultation&duration=15"
                data-testid="home-business-owners-consult-cta"
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-[#4A6FA5] text-white text-sm font-medium hover:bg-[#3B597B] transition-colors"
              >
                Schedule a 15-Minute Business Consultation <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/for-organizations"
                data-testid="home-business-owners-proposal-cta"
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg border border-slate-300 text-slate-800 text-sm font-medium hover:border-[#4A6FA5] hover:text-[#4A6FA5] transition-colors"
              >
                Request a Workforce Care Proposal
              </Link>
            </div>
            <p className="mt-5 text-[12px] text-slate-500 leading-relaxed max-w-2xl">
              Our workplace chaplaincy services are voluntary, confidential within applicable ethical and legal limits, non-clinical,
              and respectful of employees of all faith traditions and those with no religious affiliation.
            </p>
          </div>

          {/* Founder intro (item 7) — brief, professional, reuses verified language only. */}
          <aside
            className="lg:col-span-2 bg-[#F8FAFC] border border-slate-200 rounded-2xl p-6 md:p-7"
            data-testid="home-founder-intro"
            aria-labelledby="home-founder-heading"
          >
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-3">Locally led</p>
            <h3 id="home-founder-heading" className="font-heading text-xl text-slate-900 leading-snug">
              Meet Chaplain Derrick Hall
            </h3>
            <p className="mt-1 text-sm text-[#4A6FA5] font-medium">
              Founder, California Corporate Chaplains
            </p>
            <p className="mt-4 text-sm text-slate-700 leading-relaxed">
              Chaplain Derrick Hall founded California Corporate Chaplains to bring the crisis-response training,
              confidentiality, and professional support he developed in law enforcement chaplaincy into the
              corporate workplace.
            </p>
            <p className="mt-3 text-sm text-slate-700 leading-relaxed" data-testid="founder-law-enforcement">
              For five years, he served as a law enforcement chaplain across 14 agencies, providing confidential
              chaplaincy support on a 24/7 basis. His specialized training includes crisis intervention, rapid
              response, critical incident support, and active-shooter situations.
            </p>
            <p className="mt-3 text-sm text-slate-700 leading-relaxed">
              His background also includes workplace chaplaincy, hospice care, and specialized bereavement support.
              He is the author of <em>From Diagnosis to Goodbye: Restoring the Patient's Voice Through Every Stage of the Journey</em>,
              a resource dedicated to preserving dignity, strengthening communication, and honoring the voices of
              individuals and families facing serious illness.
            </p>
            <p className="mt-3 text-sm text-slate-700 leading-relaxed">
              CCCHAPS is locally led and relationship-focused, providing voluntary, non-clinical emotional support
              for employees of all faith traditions and those with no religious affiliation.
            </p>
          </aside>
        </div>
      </section>

      {/* Stats */}
      <section className="border-y border-slate-200 bg-white" data-testid="stats-band">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-2 lg:grid-cols-4 gap-6">
          {STATS.map((s) => (
            <div key={s.label}>
              <div className="font-heading text-2xl sm:text-3xl text-slate-900">{s.value}</div>
              <div className="text-xs sm:text-sm text-slate-600 mt-1 leading-relaxed">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Workplace Realities */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-3">Workplace realities</p>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight text-slate-900">
              The pressures employees carry quietly into work.
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Today's workforce is navigating layered emotional, relational, and circumstantial pressure. CCCHAPS provides confidential, non-clinical support so people don't have to carry it alone.
            </p>
          </div>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {WORKPLACE_REALITIES.map((w, i) => (
              <div key={w.title} className="bg-white border border-slate-200 rounded-xl p-6 hover:border-[#4A6FA5] hover:shadow-sm transition-all">
                <div className="text-xs text-[#4A6FA5] font-mono font-semibold">{String(i + 1).padStart(2, "0")}</div>
                <h3 className="mt-3 font-heading text-lg font-medium text-slate-900">{w.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{w.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Three pathways */}
      <section
        className="py-16 md:py-24 bg-[#F8FAFC]"
        data-testid="home-pathways-section"
        aria-labelledby="home-pathways-heading"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-10 md:mb-12">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-3">Where to next</p>
            <h2 id="home-pathways-heading" className="font-heading text-3xl sm:text-4xl tracking-tight text-slate-900">
              Three ways to get to know CCCHAPS.
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Whether you're exploring services, looking for what fits your industry, or evaluating CCCHAPS for your organization — start where it makes sense for you.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <article
              data-testid="home-pathway-services"
              data-analytics-section="services"
              className="bg-white border border-slate-200 rounded-2xl p-7 flex flex-col hover:border-[#4A6FA5] hover:shadow-md transition-all"
            >
              <span className="inline-flex items-center justify-center w-11 h-11 rounded-lg bg-[#F0F4F8] text-[#4A6FA5]">
                <Users className="w-5 h-5" strokeWidth={1.75} />
              </span>
              <h3 className="mt-5 font-heading text-xl font-medium text-slate-900">Explore Our Services</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed flex-1">
                Workplace chaplaincy, healthcare, senior living, crisis and bereavement support, and more.
              </p>
              <Link
                to="/services"
                data-testid="home-pathway-services-cta"
                className="mt-6 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-[#4A6FA5] text-white text-sm font-medium hover:bg-[#3B597B]"
              >
                Explore Services <ArrowRight className="w-4 h-4" />
              </Link>
            </article>

            <article
              data-testid="home-pathway-industries"
              data-analytics-section="industries"
              className="bg-white border border-slate-200 rounded-2xl p-7 flex flex-col hover:border-[#4A6FA5] hover:shadow-md transition-all"
            >
              <span className="inline-flex items-center justify-center w-11 h-11 rounded-lg bg-[#F0F4F8] text-[#4A6FA5]">
                <Compass className="w-5 h-5" strokeWidth={1.75} />
              </span>
              <h3 className="mt-5 font-heading text-xl font-medium text-slate-900">Industries We Serve</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed flex-1">
                Chaplain care adapted for dealerships, restaurants, construction teams, healthcare organizations, senior care communities, and other workplace settings.
              </p>
              <Link
                to="/services#industries-we-serve"
                data-testid="home-pathway-industries-cta"
                className="mt-6 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-[#4A6FA5] text-white text-sm font-medium hover:bg-[#3B597B]"
              >
                Explore Industries <ArrowRight className="w-4 h-4" />
              </Link>
            </article>

            <article
              data-testid="home-pathway-for-organizations"
              data-analytics-section="for-organizations"
              className="bg-white border border-slate-200 rounded-2xl p-7 flex flex-col hover:border-[#4A6FA5] hover:shadow-md transition-all"
            >
              <span className="inline-flex items-center justify-center w-11 h-11 rounded-lg bg-[#F0F4F8] text-[#4A6FA5]">
                <Layers className="w-5 h-5" strokeWidth={1.75} />
              </span>
              <h3 className="mt-5 font-heading text-xl font-medium text-slate-900">For Organizations</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed flex-1">
                Learn about Care Team Packages, CALLACHAP, leadership support, training, organizational resources, and ways to bring CCCHAPS to your workforce.
              </p>
              <Link
                to="/for-organizations"
                data-testid="home-pathway-for-organizations-cta"
                className="mt-6 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-[#4A6FA5] text-white text-sm font-medium hover:bg-[#3B597B]"
              >
                For Organizations <ArrowRight className="w-4 h-4" />
              </Link>
            </article>
          </div>

          <p
            className="mt-8 text-xs text-slate-500 leading-relaxed max-w-4xl"
            data-testid="home-baseline-line"
          >
            CCCHAPS care arrangements include 24/7 on-call chaplain access and digital support through CALLACHAP, the Care Portal, and CCCHAPS On Demand. Response times vary by channel.
          </p>
        </div>
      </section>

      {/* CALLACHAP feature */}
      <section
        className="py-16 md:py-24 bg-white"
        data-testid="home-callachap-feature"
        data-analytics-section="callachap"
        aria-labelledby="home-callachap-heading"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            <div className="lg:col-span-7 order-2 lg:order-1">
              <div className="flex items-center gap-3 mb-5">
                <img
                  src="/callachap-logo.png"
                  alt="CALLACHAP"
                  className="h-8 w-auto"
                  data-testid="home-callachap-feature-logo"
                />
                <span className="text-xs uppercase tracking-[0.22em] font-semibold text-[#4A6FA5]">
                  Confidential Employee Support
                </span>
              </div>
              <h2
                id="home-callachap-heading"
                className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight text-slate-900 leading-[1.08]"
              >
                Care is one click away.
              </h2>
              <div className="mt-5 space-y-4 text-slate-600 leading-relaxed max-w-2xl">
                <p>
                  CALLACHAP gives employees a simple, private way to connect with
                  chaplain support by phone, text, confidential message, or
                  available remote-support options. Employees have access to 24/7
                  on-call chaplain support, with response times varying by channel
                  and organization configuration.
                </p>
                <p>
                  CALLACHAP is included with participating CCCHAPS chaplaincy
                  arrangements — employees do not pay to use it.
                </p>
              </div>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Link
                  to="/callachap"
                  data-testid="home-callachap-learn-cta"
                  data-analytics-cta="callachap-learn-more"
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-[#4A6FA5] text-white text-sm font-medium hover:bg-[#3B597B]"
                >
                  Learn About CALLACHAP <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
            <div className="lg:col-span-5 order-1 lg:order-2">
              <div
                className="aspect-[4/5] rounded-2xl overflow-hidden shadow-sm border border-slate-200 bg-slate-100"
                data-testid="home-callachap-feature-image"
              >
                <img
                  src="/images/care-portal-callachap/call.jpg"
                  alt="A professional in warm-toned everyday clothing on a phone call outside on a city street mid-workday, illustrating how an employee might reach chaplain support through CALLACHAP."
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works — 4-Step Care Process */}
      <section
        className="py-16 md:py-24 bg-white"
        data-testid="home-how-it-works"
        aria-labelledby="home-how-it-works-heading"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-10 md:mb-14">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-3">
              How it works
            </p>
            <h2
              id="home-how-it-works-heading"
              className="font-heading text-3xl sm:text-4xl tracking-tight text-slate-900 leading-tight"
            >
              Our 4-Step Care Process
            </h2>
            <p className="mt-3 text-lg text-slate-700 leading-relaxed font-medium">
              Relationships first. Support when it matters.
            </p>
            <p className="mt-4 text-slate-600 leading-relaxed">
              California Corporate Chaplains provides relationship-based workplace care designed to help employees feel supported, give leaders another trusted resource, and strengthen a culture of care within the organization.
            </p>
          </div>

          {/* 4-step connected progression */}
          <ol
            className="relative grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5"
            data-testid="how-it-works-steps"
          >
            {[
              {
                n: "01",
                title: "Introduction",
                body: "We begin by introducing chaplaincy services to your organization and helping employees understand what support is available. Employees learn who their chaplain is, how to connect, what confidentiality means, and how workplace chaplaincy complements existing organizational resources. The goal is simple: establish familiarity before a need becomes a crisis.",
              },
              {
                n: "02",
                title: "Connection",
                body: "Trust develops through consistent presence and voluntary engagement. Depending on your organization's Care Team Package, support may include scheduled workplace visits, remote access, individual conversations, employee check-ins, and other agreed-upon points of connection. Employees decide when and how they engage. Chaplain support is voluntary, respectful, and belief-sensitive.",
              },
              {
                n: "03",
                title: "Care",
                body: "When employees face stress, grief, family concerns, serious illness, caregiving challenges, workplace difficulties, or other significant life experiences, the chaplain provides confidential, non-clinical emotional and relational support. When needs fall outside the chaplain's scope, employees can be encouraged to connect with appropriate resources such as an EAP, licensed mental-health professional, healthcare provider, community resource, or emergency service.",
              },
              {
                n: "04",
                title: "Ongoing Support",
                body: "Workplace care is most effective when it does not disappear after a single conversation. CCCHAPS provides continued relationship-based support according to the organization's service arrangement, while remaining available for emerging employee, family, leadership, and organizational needs. Organizations may also receive aggregate, non-identifying utilization and workforce-care insights while the details of confidential chaplain conversations remain private.",
              },
            ].map((s, i, arr) => (
              <li
                key={s.n}
                data-testid={`how-it-works-step-${s.n}`}
                className="relative bg-[#F8FAFC] border border-slate-200 rounded-2xl p-6 md:p-7 flex flex-col"
              >
                {/* connector dot on desktop, between cards */}
                {i < arr.length - 1 && (
                  <span
                    aria-hidden="true"
                    className="hidden lg:block absolute top-1/2 -right-3 w-2 h-2 rounded-full bg-[#4A6FA5]/40 -translate-y-1/2"
                  />
                )}
                <div className="flex items-baseline gap-3 mb-3">
                  <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-white border border-[#4A6FA5]/30 text-[#4A6FA5] font-mono text-xs font-semibold shrink-0">
                    {s.n}
                  </span>
                  <h3 className="font-heading text-lg font-medium text-slate-900 leading-tight">
                    {s.title}
                  </h3>
                </div>
                <p className="text-sm text-slate-700 leading-relaxed">{s.body}</p>
              </li>
            ))}
          </ol>

          <p
            className="mt-10 text-sm text-slate-600 leading-relaxed max-w-3xl italic"
            data-testid="how-it-works-closing"
          >
            The result is a workplace-care relationship built on presence, trust, accessibility, and appropriate support.
          </p>
        </div>
      </section>

      {/* Industries */}
      <section className="py-14 bg-[#F8FAFC] border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tight text-slate-900">
            Industries We Serve
          </h2>
          <div className="mt-6 flex flex-wrap justify-center gap-x-8 gap-y-3 text-slate-600 text-sm">
            {INDUSTRIES.map((i) => (
              <span key={i}>{i}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Care Portal call-out */}
      <section className="py-16 md:py-24 bg-[#F8FAFC]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-3">Employee Care Portal</p>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight text-slate-900">
              One Care Portal. Support for Work, Life, and Family.
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Give your workforce a private digital front door to confidential chaplain support, practical resources, and CCCHAPS On Demand — an extensive library of on-demand video resources designed to support employees, strengthen families, develop leaders, and equip teams.
            </p>
            <p className="mt-3 text-slate-600 leading-relaxed">
              <span className="font-semibold text-slate-900">Chaplain support is available 24/7 on-call</span> through phone, text, video, confidential message, and CCCHAPS On Demand where provided by the organization — included with your CCCHAPS chaplaincy service.
            </p>
            <p className="mt-3 text-slate-600 leading-relaxed">
              Employees and eligible family members can explore educational content covering mental health and emotional well-being, marriage and parenting, finances, grief and loss, resilience, personal growth, and more — including content presented by counselors, physicians, financial professionals, and other subject-matter experts.
            </p>
            <p className="mt-3 text-slate-600 leading-relaxed">
              Organizations also gain access to resources for teams and leaders, along with webinars and professional development content that can help support a healthier, more connected workplace.
            </p>
            <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5 text-sm text-slate-700">
              {[
                "Confidential chaplain support",
                "Extensive on-demand video library",
                "Mental health & emotional well-being resources",
                "Marriage, parenting & family support",
                "Financial well-being education",
                "Leadership & team development",
                "Webinars & workplace content",
                "Grief, resilience & life challenges",
                "Mobile app & on-the-go access",
              ].map((f) => (
                <li key={f} className="flex items-start gap-2.5">
                  <Check className="w-4 h-4 text-[#4A6FA5] mt-0.5 shrink-0" strokeWidth={2.5} />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/care-portal" data-testid="home-access-portal" className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-[#4A6FA5] text-white font-medium hover:bg-[#3B597B]">
                Access the Care Portal <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/for-organizations" className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg border border-slate-300 text-slate-800 font-medium hover:border-[#4A6FA5] hover:text-[#4A6FA5]">
                Request a Portal Demonstration
              </Link>
            </div>
          </div>
          <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-sm aspect-[4/3]">
            <img src="https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80" alt="Adult professional using a laptop to access on-demand resources at home" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 md:py-20 bg-[#4A6FA5]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tight text-white">Begin a conversation.</h2>
          <p className="mt-4 text-blue-100 leading-relaxed max-w-2xl mx-auto">
            Tell us a little about your team. We'll follow up confidentially within one business day — exploratory, never a pitch.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3">
              <Link to="/schedule?type=organizational_consultation&duration=15" data-testid="home-final-cta-consult" className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg bg-white text-[#4A6FA5] font-medium hover:bg-blue-50">
              Schedule a 15-Minute Business Consultation
            </Link>
            <Link to="/services/workplace-chaplaincy" data-testid="home-final-cta-services" className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg border border-blue-200 text-white font-medium hover:bg-[#3B597B]">
              Explore Employee Support Services
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
